import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Clock, Ticket } from '@phosphor-icons/react';
import { API } from '@/App';
import { useLanguage } from '@/i18n/LanguageContext';
import { getT } from '@/i18n/translations';
import PulseGrid from '@/components/PulseGrid';

const locales = { en: 'en-US', sk: 'sk-SK', de: 'de-DE' };

const TicketCounter = ({ drawId }) => {
  const [draw, setDraw] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const { language } = useLanguage();
  const text = getT('ticketCounter', language);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchDraw = async () => {
      try {
        const url = drawId ? `${API}/draws/${drawId}` : `${API}/draws/active`;
        const response = await fetch(url);
        if (response.ok && isMounted) {
          setDraw(await response.json());
        }
      } catch {
        if (isMounted) setDraw(null);
      }
    };

    fetchDraw();
    const interval = setInterval(fetchDraw, 2000);
    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [drawId]);

  if (!draw) {
    return (
      <div className="bg-[#1a1508] border border-[#cba135]/10 shadow-[0_8px_32px_rgba(0,0,0,0.6)] rounded-lg p-8">
        <div className="flex items-center justify-center gap-3 text-[#cba135]/70">
          <Ticket size={24} weight="duotone" />
          <p className="text-sm">{text.noDraw}</p>
        </div>
      </div>
    );
  }

  const sold = Number(draw.ticketsSold || 0);
  const maxTickets = Number(draw.maxTickets || 2000);
  const remaining = Math.max(maxTickets - sold, 0);
  const percentage = maxTickets > 0 ? Math.min((sold / maxTickets) * 100, 100) : 0;
  const isOpen = ['active', 'open'].includes(draw.status) && remaining > 0;
  const drawNumber = draw.drawNumber || 1;
  const statusLabel = draw.status === 'completed'
    ? `DRAW #${drawNumber} CLOSED`
    : !isOpen
      ? `DRAW #${drawNumber} LOCKED`
      : `DRAW #${drawNumber} OPEN`;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.98 }}
      animate={{ opacity: 1, scale: 1 }}
      className="bg-[#1a1508] border border-[#cba135]/10 shadow-[0_8px_32px_rgba(0,0,0,0.6)] rounded-lg p-8 relative overflow-hidden"
      data-testid="ticket-counter"
    >
      <div className="relative z-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#a8872b] mb-2">
            {text.ticketsSold}
          </p>
          <div className="heading-font text-4xl sm:text-5xl lg:text-6xl tracking-tight font-light">
            <motion.span
              key={sold}
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              className="text-[#cba135] tabular-nums"
              data-testid="tickets-sold"
            >
              {sold}
            </motion.span>
            <span className="text-[#cba135]/40"> / {maxTickets}</span>
          </div>
        </div>

        <PulseGrid />

        <div className="sm:text-right">
          <div className="inline-flex items-center gap-2 bg-green-500/10 border border-green-500/20 text-green-300 px-3 py-2 rounded-md text-xs uppercase tracking-[0.15em] font-bold mb-3">
            <span className="h-2 w-2 rounded-full bg-green-300" />
            {draw.status === 'completed' ? text.completed : text.live}
          </div>
          <div className="flex sm:justify-end items-center gap-2" data-testid="current-time-display">
            <Clock size={16} weight="duotone" className="text-[#cba135]/50" />
            <span className="text-sm text-[#cba135]/70">
              {currentTime.toLocaleDateString(locales[language] || 'en-US', { weekday: 'short', day: 'numeric', month: 'short' })}
            </span>
            <span className="text-sm font-semibold text-[#cba135] tabular-nums">
              {currentTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })}
            </span>
          </div>
        </div>
      </div>

      <div className="relative z-10 w-full h-3 bg-[#0e0b04] rounded-full overflow-hidden mb-4">
        <motion.div
          animate={{ width: `${percentage}%` }}
          transition={{ duration: 0.35, ease: 'easeOut' }}
          className="h-full bg-gradient-to-r from-[#a8872b] via-[#e6be4b] to-[#cba135] rounded-full"
          data-testid="progress-bar"
        />
      </div>

      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.5 }}
          className="relative z-10 flex justify-center mb-3"
          data-testid="draw-open-badge"
        >
          <div className="relative inline-block">
            <motion.span
              className="absolute inset-0 -m-4 rounded-full bg-[#cba135]/10 blur-2xl"
              animate={{ opacity: [0.2, 0.5, 0.2], scale: [0.95, 1.05, 0.95] }}
              transition={{ duration: 3.2, repeat: Infinity, ease: 'easeInOut' }}
            />
            <span
              className="relative heading-font tracking-[0.22em] sm:tracking-[0.28em] text-base sm:text-xl lg:text-2xl font-light uppercase gold-shimmer drop-shadow-[0_2px_18px_rgba(203,161,53,0.3)] whitespace-nowrap"
            >
              {statusLabel}
            </span>
            <motion.span
              className="absolute -inset-x-2 -bottom-0.5 h-[1px] bg-gradient-to-r from-transparent via-[#cba135]/55 to-transparent"
              animate={{ opacity: [0.3, 0.8, 0.3] }}
              transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
            />
          </div>
        </motion.div>
      )}

      <div className="relative z-10 flex justify-between text-sm text-[#cba135]/70">
        <span>{percentage.toFixed(1)}% {text.filled}</span>
        <span>{remaining} {text.remaining}</span>
      </div>
    </motion.div>
  );
};

export default TicketCounter;
